import { Checkbox } from '@fukku/checkbox'
import { Radio } from 'fukku/Radio'
import type { RadioOption } from 'fukku/Radio/Radio'
import { getFilterId } from 'product-list/context/ProductListFiltersProvider/utils'
import { FilterType } from 'product-list/types/Filters'
import type {
	CustomCSSProperties,
	OptionsProps,
} from 'product-list/types/Options'
import { dataTestIds } from 'product-list/utils/testIds'
import { type ReactNode, useCallback, useMemo } from 'react'

import styles from './Options.module.scss'

const ITEM_HEIGHT = 28
const MAX_ITEMS_PER_COLUMN = 12

export function Options({
	userFilter,
	isMobile = false,
	onChange = () => {},
	disabledValues,
	a11yLabel,
}: Readonly<OptionsProps>): ReactNode {
	const { options, type, multipleChoice, selection } = userFilter

	const controlId = getFilterId(userFilter)
	const radioOptions = useMemo<RadioOption[]>(
		() =>
			options.map(({ id, label }) => ({
				value: id,
				disabled: disabledValues.includes(id),
				content: <span className={styles['label-value']}>{label}</span>,
			})),
		[disabledValues, options]
	)

	const listStyle = useMemo(() => {
		const columnsOnDesktop =
			options.length > MAX_ITEMS_PER_COLUMN
				? MAX_ITEMS_PER_COLUMN
				: options.length
		return {
			'--column-count':
				options.length > MAX_ITEMS_PER_COLUMN && !isMobile
					? Math.ceil(options.length / MAX_ITEMS_PER_COLUMN)
					: 1,
			'--items-columns': columnsOnDesktop,
			'--item-height': ITEM_HEIGHT,
			height: isMobile
				? 'auto'
				: `calc(1px * ${columnsOnDesktop} * ${ITEM_HEIGHT})`,
		} as CustomCSSProperties
	}, [isMobile, options.length])

	const getDisabledCheckboxText = useCallback(
		(id: string, label: string) =>
			disabledValues.includes(id) ? `${label}, ${a11yLabel}` : label,
		[a11yLabel, disabledValues]
	)

	const handleChange = useCallback(
		(id: string) => {
			onChange(
				type,
				id,
				userFilter.type === FilterType.generic ? userFilter.id : undefined
			)
		},
		[onChange, type, userFilter]
	)

	if (multipleChoice) {
		return (
			<ul
				className={styles.options}
				style={listStyle}
				data-testid={dataTestIds.plpFilterDesktopGroup(isMobile)}
			>
				{options.map(({ id, label }) => (
					<li
						key={id}
						data-testid={dataTestIds.plpFilterDesktopItem(id, isMobile)}
					>
						<Checkbox
							id={id}
							className={
								isMobile ? styles.checkboxMobile : styles.checkboxDesktop
							}
							aria-label={getDisabledCheckboxText(id, label)}
							disabled={disabledValues.includes(id)}
							checked={userFilter.selection.includes(id)}
							label={label}
							onChange={() => handleChange(id)}
						/>
					</li>
				))}
			</ul>
		)
	}

	return (
		<Radio
			className={isMobile ? styles.radiosMobile : styles.radios}
			legend={userFilter.label}
			id={controlId}
			data-testid={dataTestIds.plpFilterMobileType(type)}
			name={type}
			defaultSelected={selection[0]}
			onChange={(valueId) => handleChange(valueId.toString())}
			options={radioOptions}
		/>
	)
}
